import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Stack, SxProps } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { flexBase } from '../../../Constants';
import { FieldType } from '../../../redux/project/documents/sliceProjectDocuments';
import { ProjectSidepanel } from '../../../redux/project/sliceProjectSidepanel';
import { parseDate } from '../../../util/DataUtils';
import { useOpenSidePanel } from '../../../hooks/useOpenSidepanel';
import AppTooltip from '../AppTooltip/AppTooltip';
import DocAdditionalIcon from '../Icons/DocAdditionalIcon';
import DocStatusIcon from '../Icons/DocStatusIcon';
import DocTypeIcon from '../Icons/DocTypeIcon';
import DocTableFileInfo from './DocTableFileInfo';
import DocTableHoverButtons from './DocTableHoverButtons';
import DocTableIndicator from './DocTableIndicator';
import DocTableCellCategoryAutomatic from './DocTableCellCategoryAutomatic';
import ImportTableCategoryCell from '../ImportTable/ImportTableCategoryCell';
import ImportTableEditableCell from '../ImportTable/ImportTableEditableCell';
import { getHighlightedText } from '../../../util/StringUtils';
import DocTableMultiline from './DocTableMultiline';
import { headingFontBold, vars } from '../../../Theme';
import { mdiCommentEdit } from '@mdi/js';
import Icon from '@mdi/react';

/** Base styles for the outer element. */
const boxSx: SxProps = {
    ...flexBase,
    width: '100%',
    height: '100%',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
};

/**
 * Default Component for cells in the DocTable.
 */
const DefaultDocTableCell = ({
    type,
    row,
    value,
    hoverBtns,
    isEditable,
    highlightText = '',
}: IDocTableCellProps) => {
    /** State slice that controls the visibility of the cell's hover buttons. */
    const [showHoverBtns, setShowHoverBtns] = useState<boolean>(false);

    /** Parse value to date if necessary. */
    const newValue = type === 'date' ? parseDate(value) : value;

    /** Create the table cell's contents. */
    const cellContent = newValue !== null &&
        newValue !== undefined &&
        typeof newValue === 'string' && (
            <AppTooltip title={newValue} placement='top-start'>
                <Box
                    sx={{
                        flex: 1,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                >
                    {getHighlightedText(newValue, highlightText)}
                </Box>
            </AppTooltip>
        );

    return (
        <Box
            sx={boxSx}
            onMouseEnter={() => {
                hoverBtns && setShowHoverBtns(true);
            }}
            onMouseLeave={() => setShowHoverBtns(false)}
        >
            {cellContent}

            {showHoverBtns && <DocTableHoverButtons document={row} isEditable={isEditable} />}
        </Box>
    );
};

/**
 * Component that renders a cell in the DocTable.
 */
export default ({
    type,
    row,
    value,
    hoverBtns,
    disableClick,
    notesField,
    isEditable,
    highlightText = '',
}: IDocTableCellProps) => {
    /** Deconstruct the "row" property. */
    const { UniversalID, ChildCount, RedliningCount, Files, DocNumber, WfId, DocIndexNext } = row;

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /**
     * Hook for opening the sidepanel when the user clicks on the main component element.
     */
    const handleClick = useOpenSidePanel({
        UniversalId: row.UniversalID,
        NoteId: row.NoteId,
        disableClick,
    });

    /**
     * Opens a new page in order to view the workflow history for the document.
     */
    const handleClickHistory = () => {
        window.open(`/workflow-loader/${replicaId}/${UniversalID}/${DocNumber}`);
    };

    /** Base styles for clickable Stack components. */
    const stackSx: SxProps = {
        flexDirection: 'row',
        alignItems: 'center',
        cursor: 'pointer',
    };

    /** Pointer condition for wfHistory case. */
    const pointerSx: SxProps = {
        pointerEvents: WfId ? 'pointer' : 'none',
    };

    switch (type) {
        case 'icon':
            return (
                <Stack
                    onClick={e => {
                        handleClick(e, ProjectSidepanel.General);
                    }}
                    sx={{ ...stackSx }}
                >
                    <DocTypeIcon
                        type={value}
                        color='inherit'
                        tooltipPlacement='top-start'
                        testMode={row.TestMode}
                    />
                </Stack>
            );

        case 'files':
            return (
                <>
                    <Stack
                        onClick={e => handleClick(e, ProjectSidepanel.Files)}
                        sx={{ ...stackSx, minWidth: 60 }}
                    >
                        <DocTableFileInfo fileInfo={Files} />
                    </Stack>

                    <Stack
                        onClick={e => handleClick(e, ProjectSidepanel.Documents)}
                        sx={{ ...stackSx, minWidth: 30, flexGrow: 0 }}
                    >
                        <DocAdditionalIcon count={ChildCount} />
                    </Stack>
                </>
            );

        case 'redlinings':
            return (
                <>
                    {RedliningCount > 0 && (
                        <Stack
                            onClick={e => handleClick(e, ProjectSidepanel.Documents)}
                            sx={{ ...stackSx, minWidth: 30 }}
                        >
                            <AppTooltip title={RedliningCount}>
                                <Stack direction='row' alignItems='center'>
                                    <Icon path={mdiCommentEdit} color={vars.red} size={1} />
                                    <Box
                                        sx={{
                                            ...headingFontBold,
                                            fontSize: 12,
                                        }}
                                    >
                                        {RedliningCount > 0 && `+${RedliningCount}`}
                                    </Box>
                                </Stack>
                            </AppTooltip>
                        </Stack>
                    )}
                </>
            );

        case 'status':
            return (
                <Stack
                    onClick={e => {
                        handleClick(e, ProjectSidepanel.History);
                    }}
                    sx={{ ...stackSx }}
                >
                    <Box>{value}</Box>
                </Stack>
            );

        case 'wfHistory':
            return (
                <Stack
                    onClick={handleClickHistory}
                    sx={{
                        ...stackSx,
                        ...pointerSx,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <DocStatusIcon
                        type={value}
                        document={row}
                        tooltip
                        tooltipPlacement='top-start'
                    />

                    {DocIndexNext && (
                        <Box
                            sx={{
                                paddingLeft: '4px',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                            }}
                        >
                            [{DocIndexNext}]
                        </Box>
                    )}
                </Stack>
            );
        case 'multiline':
            return <DocTableMultiline value={value} />;

        case 'dateRelative':
            return <DocTableIndicator value={value} isDate />;

        case 'numberRelative':
            return <DocTableIndicator value={+value} />;

        case 'categoryAutomatic':
            return (
                <DocTableCellCategoryAutomatic
                    replicaId={replicaId}
                    documentId={UniversalID}
                    value={value}
                />
            );

        case 'importCategory':
            return (
                <ImportTableCategoryCell
                    mode='Category'
                    replicaId={replicaId}
                    documentId={UniversalID}
                    value={value}
                />
            );

        case 'importPackage':
            return (
                <ImportTableCategoryCell
                    mode='Package'
                    replicaId={replicaId}
                    documentId={UniversalID}
                    value={value}
                />
            );

        case 'creationDate':
            return <p>{value}</p>;

        case 'editableString':
            return (
                <ImportTableEditableCell
                    replicaId={replicaId}
                    docId={UniversalID}
                    value={value}
                    notesField={notesField}
                />
            );

        default:
            return (
                <DefaultDocTableCell
                    {...{ row, type, value, hoverBtns, notesField, isEditable, highlightText }}
                />
            );
    }
};

export interface IDocTableCellBaseProps {
    /** Cell value. */
    value: string;
}

/**
 * Interface that describes the props for a DocTableCell component.
 */
export interface IDocTableCellProps extends IDocTableCellBaseProps {
    /** The type of field that the cell is showing. */
    type: FieldType;

    /** The DataGrid row associated with this cell. */
    row: GridValidRowModel;

    /** The name of the corresponding field in Notes. */
    notesField: string;

    /** (OPTIONAL) Defines hover buttons within the cell. */
    hoverBtns?: boolean;

    /** (OPTIONAL) Flag that prevents on-click events for cells being triggered. */
    disableClick?: boolean;

    /** (OPTIONAL) Defines if field is editable*/
    isEditable?: boolean;

    highlightText?: string;
}

export interface IDocTableEditCellProps extends IDocTableCellBaseProps {
    onUpdateValue?: (newValue: string) => void;
}
